import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, AlertTriangle, FileText } from 'lucide-react';

/**
 * Structured decision package from Hermes / MCP draft tools.
 */
export default function AIAgentDecisionPackage({
  package: pkg,
  onConfirm,
  onCancel,
  isProcessing = false,
  lightSurface = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.decisionPackage.${key}`, opts);

  if (!pkg) return null;

  const border = lightSurface ? 'border-blue-200 bg-blue-50' : 'border-blue-400/30 bg-blue-500/10';
  const text = lightSurface ? 'text-gray-800' : 'text-white/95';
  const muted = lightSurface ? 'text-gray-600' : 'text-white/70';

  return (
    <div className={`rounded-xl border p-4 ${border} ${text}`}>
      <div className="flex items-start gap-2 mb-3">
        <FileText className="h-5 w-5 shrink-0 mt-0.5 opacity-80" />
        <div>
          <p className="font-semibold text-sm">{p('title')}</p>
          <p className={`text-xs ${muted}`}>{pkg.title || pkg.moduleId || p('default_title')}</p>
        </div>
      </div>

      {pkg.evidence ? (
        <section className="mb-3">
          <p className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{p('evidence')}</p>
          <p className="text-sm whitespace-pre-wrap">{pkg.evidence}</p>
        </section>
      ) : null}

      {pkg.risks ? (
        <section className="mb-3 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400 mt-0.5" />
          <div>
            <p className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{p('risks')}</p>
            <p className="text-sm whitespace-pre-wrap">{pkg.risks}</p>
          </div>
        </section>
      ) : null}

      {pkg.recommendation ? (
        <section className="mb-4">
          <p className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{p('recommendation')}</p>
          <p className="text-sm whitespace-pre-wrap">{pkg.recommendation}</p>
        </section>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={isProcessing}
          onClick={onConfirm}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white"
        >
          <CheckCircle2 className="h-4 w-4" />
          {p('approve')}
        </button>
        <button
          type="button"
          disabled={isProcessing}
          onClick={onCancel}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${
            lightSurface
              ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
              : 'border-white/20 text-white/90 hover:bg-white/10'
          }`}
        >
          <XCircle className="h-4 w-4" />
          {p('reject')}
        </button>
      </div>
    </div>
  );
}
